import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog?published=true`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <section className="section-padding bg-white" data-testid="blog-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="blog-title"
            >
              Espace Lecteur
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto leading-relaxed">
              Articles, nouvelles et insights sur nos activités et le développement communautaire.
            </p>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div>
            </div>
          )}
          {!loading && posts.length === 0 && (
            <div className="text-center py-12" data-testid="no-posts-message">
              <p className="text-[#5C665A]">Aucun article disponible pour le moment.</p>
            </div>
          )}
          {!loading && posts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link 
                  key={post.post_id} 
                  to={`/blog/${post.post_id}`}
                  className="card group"
                  data-testid={`blog-post-${post.post_id}`}
                >
                  {post.image_url && (
                    <img 
                      src={post.image_url} 
                      alt={post.title}
                      className="w-full h-48 object-cover rounded-lg mb-4 -mx-6 -mt-6 group-hover:opacity-90 transition-opacity"
                    />
                  )}
                  <div className="flex items-center justify-between text-sm text-[#5C665A] mb-3">
                    <div className="flex items-center space-x-2">
                      <User size={16} />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar size={16} />
                      <span>{new Date(post.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  <h3 
                    className="text-xl font-bold text-[#2E332D] mb-3 group-hover:text-[#4A6B48] transition-colors"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {post.title}
                  </h3>
                  <p className="text-sm text-[#5C665A] leading-relaxed mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center text-[#4A6B48] text-sm font-medium group-hover:underline">
                    Lire la suite
                    <ArrowRight className="ml-2" size={16} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
